import { useState, type CSSProperties } from 'react';
import type { EquippedCosmetics } from '@anidex/shared/economy';
import type { PlayerSummaryDTO, UserDTO } from '@anidex/shared/types';
import { AvatarFx, AvatarFxBack, FrameRing } from './cosmetics/Cosmetics';

export type AvatarPlayer =
  | (Pick<UserDTO, 'username' | 'avatar'> & { cosmetics?: EquippedCosmetics })
  | (Pick<PlayerSummaryDTO, 'username' | 'avatar' | 'level'> & { cosmetics?: EquippedCosmetics });

function progressOf(user: AvatarPlayer & { level?: UserDTO['level'] | number }): number {
  const level = 'level' in user ? user.level : undefined;
  if (level && typeof level === 'object' && 'progress' in level) return Math.round(level.progress * 100);
  return 0;
}

const ANIMATED_FROM = 56;

export function Avatar({ user, size = 64 }: { user: AvatarPlayer; size?: number }) {
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const imageUrl = user.avatar?.imageUrl;
  const portrait = imageUrl && imageUrl !== failedImage ? (
    <img src={imageUrl} alt="" width={size} height={size} onError={() => setFailedImage(imageUrl)} />
  ) : (
    <span className="avatar__initial">{user.username.slice(0, 1).toUpperCase()}</span>
  );
  const progress = progressOf(user as AvatarPlayer & { level?: UserDTO['level'] | number });
  const style = { width: size, height: size, '--progress': `${progress}%`, '--avatar-size': `${size}px` } as CSSProperties;
  const frame = user.cosmetics?.frame;
  const fx = user.cosmetics?.avatar;
  if (!frame && !fx) {
    return (
      <span className="avatar" style={style}>
        <span className="avatar__mask">
          {portrait}
        </span>
      </span>
    );
  }
  return (
    <span
      className={`avatar-slot${size < ANIMATED_FROM ? ' is-still' : ''}`}
      style={{ width: size, height: size, '--slot': `${size}px` } as CSSProperties}
    >
      {fx ? <AvatarFxBack fxKey={fx} /> : null}
      <span className="avatar" style={style}>
        <span className="avatar__mask">
          {portrait}
        </span>
      </span>
      {frame ? <FrameRing frameKey={frame} /> : null}
      {fx ? <AvatarFx fxKey={fx} /> : null}
    </span>
  );
}
